import React from 'react'
import { useLocation } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ModeToggle } from '@/components/mode-toggle'

interface PageHeaderProps {
  title?: string
  children?: React.ReactNode
  onMenuClick?: () => void
  showMenuButton?: boolean
}

const pageConfig = {
  // '/home' intentionally has no entry: the Build tab is a full-page chat that
  // needs no title, and the Overview tab renders its own heading in Home.tsx.
  '/network-policies': {
    title: 'Network Policy Recommendations', 
    description: 'View and manage Kubernetes network policy recommendations across your clusters'
  },
  '/network-policy-effectiveness': {
    title: 'Network Policy Effectiveness Analysis',
    description: 'Analyze existing network policies from your clusters and their effectiveness metrics'
  },
  '/topology-map': {
    title: 'Cloud Topology Map',
    description: 'Navigate through Cloud resources and Kubernetes clusters in an interactive graph view.'
  },
  '/traffic-insights': {
    title: 'Traffic Insights',
    description: 'AI-powered analysis of network traffic patterns and insights across your Cloud.'
  },
  '/cloud-ai-copilot': {
    title: 'Cloud AI Copilot',
    description: 'Chat with an AI copilot to manage your cloud infrastructure.'
  },
  '/chat-changelog': {
    title: 'Chat Configuration Changes',
    description: 'Review and manage configuration changes generated from AI chat sessions.'
  },
  '/onboard-cluster': {
    title: 'Onboard a New Cluster',
    description: 'Manage operator credentials for your clusters. Generate new tokens to onboard clusters or revoke access by deleting existing tokens.'
  },
  '/integrations': {
    title: 'Integrations',
    description: 'Connect Kestrel to your platform stack. Browse available integrations and manage their connections.'
  },
  '/team-management': {
    title: 'Organization Management',
    description: 'Manage your organization, invite team members, and set permissions.'
  },
  '/risk-assessment': {
    title: 'Risk Assessment',
    description: 'Monitor and manage autonomous security risk assessments across your Cloud'
  },
  '/authorization-policies': {
    title: 'Authorization Policies',
    description: 'View and manage Istio authorization policy recommendations across your clusters'
  },
  '/authorization-policy-effectiveness': {
    title: 'Authorization Policy Effectiveness',
    description: 'Analyze existing authorization policies from your clusters and their effectiveness metrics'
  },
  '/vulnerability-dashboard': {
    title: 'Vulnerability Dashboard',
    description: 'Security findings across your entire Cloud'
  },
  '/network-policy-simulations': {
    title: 'Network Policy Simulations',
    description: 'Create and analyze custom network policy traffic simulations'
  },
  '/authorization-policy-simulations': {
    title: 'Authorization Policy Simulations',
    description: 'Create and analyze custom authorization policy L7 traffic simulations'
  },
  '/cloud-integrations': {
    title: 'Cloud Integrations',
    description: 'Connect AWS, GCP, and OCI accounts to power cloud workflow automation.'
  },
  '/inventory': {
    title: 'Cloud Inventory',
    description: 'Browse and search all Cloud resources with incident insights.'
  },
  '/cicd-integration': {
    title: 'CI/CD Integrations',
    description: 'Connect your IaC and application code repositories for workflows, incident response, and cloud AI copilot.'
  },
  '/integrations/slack': {
    title: 'Slack Integration',
    description: 'Receive real-time incident notifications, AI-powered Q&A, and workflow messages directly in Slack.'
  },
  '/integrations/pagerduty': {
    title: 'PagerDuty Integration',
    description: 'Route incident alerts to on-call teams, trigger RCAs from PagerDuty incidents, and manage alerts via workflows.'
  },
  '/integrations/argocd': {
    title: 'ArgoCD Integration',
    description: 'Auto-detected on clusters running the Kestrel operator — trigger application syncs and monitor deployment health from workflows.'
  },
  '/integrations/argo-rollouts': {
    title: 'Argo Rollouts Integration',
    description: 'Promote, abort, retry, and roll back canary and blue-green rollouts from workflows. Detected automatically by the Kestrel operator.'
  },
  '/integrations/fluxcd': {
    title: 'Flux CD Integration',
    description: 'Reconcile, suspend, and resume Flux Kustomizations, HelmReleases, and sources from workflows. Detected automatically by the Kestrel operator.'
  },
  '/integrations/karpenter': {
    title: 'Karpenter Integration',
    description: 'Scale NodePool limits, tune disruption policies, and recycle nodes from workflows. Detected automatically by the Kestrel operator.'
  },
  '/integrations/kyverno': {
    title: 'Kyverno Integration',
    description: 'React to policy violations and admission blocks, and manage policies from workflows. Detected automatically by the Kestrel operator.'
  },
  '/integrations/trivy': {
    title: 'Trivy Integration',
    description: 'React to CVEs, exposed secrets, misconfigurations, and compliance failures from workflows. Detected automatically by the Kestrel operator.'
  },
  '/integrations/datadog': {
    title: 'Datadog Integration',
    description: 'Query Datadog metrics and monitors from workflows and incident investigations. Detected automatically by the Kestrel operator.'
  },
  '/integrations/posthog': {
    title: 'PostHog Integration',
    description: 'Connect PostHog for session replay analysis, AI summaries, and event queries in workflows.'
  },
  '/integrations/vercel': {
    title: 'Vercel Integration',
    description: 'Connect Vercel for deployment management, build logs, and observability workflows.'
  },
  '/integrations/railway': {
    title: 'Railway Integration',
    description: 'Connect Railway for deployment monitoring, log analysis, rollback, redeploy, and variable automation in workflows.'
  },
  '/integrations/flyio': {
    title: 'Fly.io Integration',
    description: 'Connect Fly.io for machine lifecycle monitoring, secrets management, and investigation automation in workflows.'
  },
  '/integrations/nebius': {
    title: 'Nebius Integration',
    description: 'Connect Nebius AI Cloud for GPU instance lifecycle monitoring, managed Kubernetes scaling, and investigation automation in workflows.'
  },
  '/integrations/beam': {
    title: 'Beam Integration',
    description: 'Connect Beam for serverless deployment, container and sandbox lifecycle, task monitoring, and investigation automation in workflows.'
  },
  '/integrations/daytona': {
    title: 'Daytona Integration',
    description: 'Connect Daytona for sandbox, snapshot, and volume lifecycle, command execution, and investigation automation in workflows.'
  },
  '/integrations/supabase': {
    title: 'Supabase Integration',
    description: 'Connect Supabase for project, branch, backup, and read-replica lifecycle, network restrictions, and investigation automation in workflows.'
  },
  '/integrations/planetscale': {
    title: 'PlanetScale Integration',
    description: 'Connect PlanetScale for database branch, deploy-request (schema migration), backup, and credential automation in workflows.'
  },
  '/integrations/neon': {
    title: 'Neon Integration',
    description: 'Connect Neon for serverless Postgres branch lifecycle, compute suspend/resume, autoscaling, and credential rotation in workflows.'
  },
  '/integrations/clickhouse': {
    title: 'ClickHouse Integration',
    description: 'Connect ClickHouse Cloud for service lifecycle, scaling, backups, ClickPipes ingestion, and cost automation in workflows.'
  },
  '/integrations/jenkins': {
    title: 'Jenkins Integration',
    description: 'Connect Jenkins for build triggering, job monitoring, queue management, node health, and build failure investigation in workflows.'
  },
  '/integrations/circleci': {
    title: 'CircleCI Integration',
    description: 'Connect CircleCI for pipeline triggering, workflow rerun/cancel, job approval, insights, and failure investigation in workflows.'
  },
  '/integrations/sonarcloud': {
    title: 'SonarCloud Integration',
    description: 'Connect SonarCloud to react to quality gate results and query issues, security hotspots, and code quality metrics in workflows.'
  },
  '/integrations/okta': {
    title: 'Okta Integration',
    description: 'Connect Okta to react to identity events from the System Log and manage users, groups, sessions, and app assignments in workflows.'
  },
  '/integrations/databricks': {
    title: 'Databricks Integration',
    description: 'Connect Databricks to react to job, cluster, and pipeline events and automate data platform operations in workflows.'
  },
  '/integrations/cloudflare': {
    title: 'Cloudflare Integration',
    description: 'Manage DNS, security, cache, Workers, and edge infrastructure.'
  },
  '/integrations/terraform': {
    title: 'Terraform Cloud Integration',
    description: 'Connect Terraform Cloud to queue and gate runs, read state outputs, manage variables, respond to drift, and investigate failures in workflows.'
  },
  '/integrations/pulumi': {
    title: 'Pulumi Cloud Integration',
    description: 'Connect Pulumi Cloud to run and gate deployments, read stack outputs, respond to drift and policy violations, and investigate failures in workflows.'
  },
  '/integrations/vault': {
    title: 'HashiCorp Vault Integration',
    description: 'Connect Vault for KV secret lifecycle, policy and auth-method monitoring, seal-status alerts, and secret rotation automation in workflows.'
  },
  '/integrations/infisical': {
    title: 'Infisical Integration',
    description: 'Connect Infisical for secret lifecycle, folder and sync management, approval-request routing, and audit-event automation in workflows.'
  },
  '/deploy': {
    title: 'Deploy Applications',
    description: 'Analyze your GitHub repositories to discover and deploy applications'
  },
  '/billing': {
    title: 'Billing & Subscriptions',
    description: 'Manage your subscription, view billing history, and update payment information.'
  },
  '/incidents': {
    title: 'Kubernetes Incidents',
    description: 'AI-powered detection and analysis of Kubernetes application and infrastructure incidents.'
  },
  '/cloud-incidents': {
    title: 'Cloud Incidents',
    description: 'AI-powered detection and analysis of cloud application and infrastructure incidents.'
  },
  '/integrations/knowledge-sources': {
    title: 'Knowledge Sources',
    description: 'Connect your organization\'s knowledge sources to enhance incident response and enable workflow automations.'
  },
  '/on-prem': {
    title: 'On-Premises Deployment',
    description: 'Deploy Kestrel in your AWS environment with full control over your data.'
  },
  '/settings': {
    title: 'Settings',
    description: 'Manage your profile, security, and organization settings.'
  },
  '/workflows': {
    title: 'Workflows',
    description: 'Automate operations with signal-driven, composable pipelines.'
  },
  '/workflows/suggestions': {
    title: 'Workflow Library',
    description: 'AI-suggested workflows based on your integrations and runbooks.'
  },
  '/workflow-approvals': {
    title: 'Pending Approvals',
    description: 'Workflow steps waiting for your approval before they can continue.'
  },
  '/workflow-requests': {
    title: 'Workflow Requests',
    description: 'Requests from your team that need a workflow or manual approval.'
  },
  '/workflow-observability': {
    title: 'Workflow Observability',
    description: 'Analytics and insights across your workflows.'
  },
  '/workflow-executions': {
    title: 'Workflow Executions',
    description: 'Browse, filter, and inspect all workflow execution runs.'
  },
  '/api-keys': {
    title: 'API Keys',
    description: 'Create and manage API keys for programmatic access via the SDK, CLI, and MCP.'
  }
}

export function PageHeader({ title, children, onMenuClick, showMenuButton }: PageHeaderProps) {
  const location = useLocation()
  const config = pageConfig[location.pathname as keyof typeof pageConfig]
  
  // Don't render PageHeader for detail pages or pages with custom headers
  const isNetworkPolicyDetail = location.pathname.startsWith('/network-policies/') && location.pathname !== '/network-policies'
  const isNetworkPolicyEffectivenessDetail = location.pathname.startsWith('/network-policy-effectiveness/') && location.pathname !== '/network-policy-effectiveness'
  const isNetworkPolicySimulationDetail = location.pathname.startsWith('/network-policy-simulations/') && location.pathname !== '/network-policy-simulations'
  const isAuthorizationPolicySimulationDetail = location.pathname.startsWith('/authorization-policy-simulations/') && location.pathname !== '/authorization-policy-simulations'
  const isRiskAssessmentDetail = location.pathname.startsWith('/risk-assessment/') && location.pathname !== '/risk-assessment'
  const isAuthorizationPolicyDetail = location.pathname.startsWith('/authorization-policies/') && location.pathname !== '/authorization-policies'
  const isAuthorizationPolicyEffectivenessDetail = location.pathname.startsWith('/authorization-policy-effectiveness/') && location.pathname !== '/authorization-policy-effectiveness'
  const isEKSClusterProgressPage = location.pathname.startsWith('/cluster-progress/')
  const isIncidentPage = location.pathname === '/incidents' // IncidentResponse has its own header
  const isIncidentDetail = location.pathname.startsWith('/incidents/') && location.pathname !== '/incidents'
  const isCloudIncidentDetail = location.pathname.startsWith('/cloud-incidents/') && location.pathname !== '/cloud-incidents'
  const isFindingDetail = location.pathname.startsWith('/findings/') // K8s finding detail page
  const isCloudFindingDetail = location.pathname.startsWith('/cloud-findings/') // Cloud finding detail page
  const isSecurityRecommendationsPage = location.pathname === '/security-recommendations' // Has its own header
  const isSecurityEffectivenessPage = location.pathname === '/security-effectiveness' // Has its own header
  const isWorkflowBuilder = location.pathname === '/workflows/new' || (location.pathname.startsWith('/workflows/') && location.pathname.endsWith('/edit'))
  const isWorkflowDetail = location.pathname.startsWith('/workflows/') && location.pathname !== '/workflows' && !location.pathname.endsWith('/edit') && location.pathname !== '/workflows/suggestions'
  if (isNetworkPolicyDetail || isNetworkPolicyEffectivenessDetail || isNetworkPolicySimulationDetail || isAuthorizationPolicySimulationDetail || isRiskAssessmentDetail || isAuthorizationPolicyDetail || isAuthorizationPolicyEffectivenessDetail || isEKSClusterProgressPage || isIncidentPage || isIncidentDetail || isCloudIncidentDetail || isFindingDetail || isCloudFindingDetail || isSecurityRecommendationsPage || isSecurityEffectivenessPage || isWorkflowBuilder || isWorkflowDetail) {
    return null
  }
  
  const pageTitle = title || config?.title || ''
  const pageDescription = config?.description || ''

  return (
    <div className="bg-background">
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="relative flex items-center justify-between">
          {/* Centered slot pages can portal controls into (e.g. Home's
              Build/Overview toggle), vertically aligned with the ModeToggle. */}
          <div
            id="page-header-center-slot"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center"
          />
          <div className="flex items-center flex-1 min-w-0 pr-4 gap-3">
            {showMenuButton && (
              <Button variant="ghost" size="sm" onClick={onMenuClick} className="p-2 -ml-2 flex-shrink-0" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            )}
            {pageTitle && (
              <div className="max-w-4xl">
                <h1 className="text-2xl font-bold text-foreground">{pageTitle}</h1>
                {pageDescription && (
                  <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{pageDescription}</p>
                )}
              </div>
            )}
            {children}
          </div>
          <div className="flex items-center flex-shrink-0">
            <ModeToggle />
          </div>
        </div>
      </div>
    </div>
  )
} 